interface ServiceItem {
  icon: string;
  title: string;
  desc: string;
}

interface InnerServicesSectionProps {
  sectionLabel: string;
  h2: string;
  intro?: string;
  items: ServiceItem[];
  featureImage: string;
  featureTitle: string;
  featureDesc: string;
  onOpenConsultation: () => void;
}

export default function InnerServicesSection({
  sectionLabel,
  h2,
  intro,
  items,
  featureImage,
  featureTitle,
  featureDesc,
  onOpenConsultation,
}: InnerServicesSectionProps) {
  return (
    <section className="py-28 bg-offWhite">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-gold"></span>
              <p className="text-gold text-xs font-bold uppercase tracking-[0.18em]">{sectionLabel}</p>
            </div>
            <h2 className="font-heading text-navy font-bold leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
              {h2}
            </h2>
            {intro && (
              <p className="text-gray-500 text-base mt-4 max-w-lg leading-relaxed">{intro}</p>
            )}
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Featured card */}
          <button
            onClick={onOpenConsultation}
            className="lg:row-span-2 relative overflow-hidden rounded-2xl group min-h-[420px] text-left cursor-pointer"
          >
            <img
              src={featureImage}
              alt={featureTitle}
              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44] via-[#0F2A44]/55 to-[#0F2A44]/10" />
            <div className="absolute inset-x-0 bottom-0 p-8 z-10">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm mb-4">
                <i className="ri-user-heart-line text-white text-xl"></i>
              </div>
              <h3 className="font-heading text-white text-2xl font-bold mb-3 leading-tight">{featureTitle}</h3>
              <p className="text-white/65 text-sm leading-relaxed mb-5">{featureDesc}</p>
              <span className="inline-flex items-center gap-2 text-gold text-sm font-bold group-hover:gap-3 transition-all">
                Get Started <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </button>

          {/* Service cards */}
          {items.map((service) => (
            <div
              key={service.title}
              className="group flex flex-col bg-white rounded-2xl p-7 border border-gray-100/80 hover:border-sage hover:-translate-y-1 transition-all relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-28 h-28 bg-sage/5 rounded-full -translate-y-10 translate-x-10 group-hover:bg-sage/10 transition-colors pointer-events-none" />
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-sage/15 mb-5 group-hover:bg-sage/25 transition-colors">
                <i className={`${service.icon} text-sage text-xl`}></i>
              </div>
              <h3 className="font-heading text-navy text-lg font-bold mb-2.5 leading-tight">{service.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed flex-1">{service.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div
          className="mt-14 rounded-2xl overflow-hidden relative"
          style={{ background: 'linear-gradient(120deg, #0F2A44 0%, #163650 50%, #0d3528 100%)' }}
        >
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, rgba(200,169,106,0.35) 0%, transparent 55%)' }} />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 px-9 py-8">
            <div>
              <p className="text-gold text-xs font-bold uppercase tracking-[0.18em] mb-2">Free, No-Obligation</p>
              <h3 className="font-heading text-white text-2xl font-bold leading-tight">Have questions about this service?</h3>
              <p className="text-white/55 text-sm mt-1.5">Our care coordinators are ready to help you choose the right plan.</p>
            </div>
            <div className="flex flex-wrap gap-3 flex-shrink-0">
              <button
                onClick={onOpenConsultation}
                className="whitespace-nowrap bg-gold text-navy font-bold px-8 py-3.5 rounded-full hover:brightness-110 transition-all cursor-pointer text-sm"
              >
                Schedule Consultation
              </button>
              <a
                href="tel:2173462042"
                className="whitespace-nowrap flex items-center gap-2 bg-white/10 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/20 transition-all text-sm border border-white/20"
              >
                <i className="ri-phone-fill text-gold"></i>
                (217) 346-2042
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
